import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import {
  User,
  X,
  Search,
  Plus,
  MessageSquare,
  Heart,
  Code,
  CreditCard,
  Settings,
  HelpCircle,
  ChevronRight,
  LogOut,
} from "lucide-react";
import { supabase } from "../../lib/supabase";

interface UserMenuProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  menuRef: React.RefObject<HTMLDivElement>;
}

export function UserMenu({ user, isOpen, onClose, menuRef }: UserMenuProps) {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/");
      onClose();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <div
      ref={menuRef}
      className={`fixed inset-y-0 left-0 z-50 w-80 bg-[#161b22] border-r border-[#30363d] shadow-xl transform transition-transform duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="p-6 flex flex-col h-full">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-semibold text-white">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-[#58a6ff] hover:bg-[#1f6feb]/10"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User info */}
        <div className="bg-[#0d1117] rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-[#1f6feb]/20 flex items-center justify-center mr-4">
              <User className="h-6 w-6 text-[#58a6ff]" />
            </div>
            <div className="truncate">
              <div className="font-medium text-white truncate">
                {user.email}
              </div>
              <div className="text-sm text-gray-400">Free Trial</div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search"
              className="w-full pl-10 pr-4 py-2 bg-[#0d1117] border border-[#30363d] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#1f6feb]"
            />
          </div>
        </div>

        {/* New Pattern button */}
        <Link
          to="/generator"
          className="w-full flex items-center justify-center gap-2 p-3 bg-[#238636] text-white rounded-lg hover:bg-[#2ea043] transition-colors mb-6"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
            navigate("/generator");
          }}
        >
          <Plus className="h-4 w-4" />
          <span>New Pattern</span>
        </Link>

        {/* Your Patterns section */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Your Patterns
          </h3>
          <div className="space-y-1">
            <Link
              to="/dashboard"
              className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate("/dashboard");
              }}
            >
              <div className="flex items-center">
                <MessageSquare className="h-4 w-4 mr-3 text-gray-400" />
                <span className="text-sm">Recent Patterns</span>
              </div>
            </Link>
            <Link
              to="/dashboard/blocks"
              className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate("/dashboard/blocks");
              }}
            >
              <div className="flex items-center">
                <Heart className="h-4 w-4 mr-3 text-gray-400" />
                <span className="text-sm">Saved Patterns</span>
              </div>
            </Link>
            <Link
              to="/templates"
              className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate("/templates");
              }}
            >
              <div className="flex items-center">
                <Code className="h-4 w-4 mr-3 text-gray-400" />
                <span className="text-sm">Templates</span>
              </div>
            </Link>
          </div>
        </div>

        {/* Menu items */}
        <nav className="flex-1 space-y-1">
          <Link
            to="/pricing"
            className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              navigate("/pricing");
            }}
          >
            <div className="flex items-center">
              <CreditCard className="h-4 w-4 mr-3 text-gray-400" />
              <span className="text-sm">Subscription</span>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </Link>

          <Link
            to="/settings"
            className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              navigate("/settings");
            }}
          >
            <div className="flex items-center">
              <Settings className="h-4 w-4 mr-3 text-gray-400" />
              <span className="text-sm">Settings</span>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </Link>

          <Link
            to="/docs"
            className="flex items-center justify-between p-2 rounded-lg text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
              navigate("/docs");
            }}
          >
            <div className="flex items-center">
              <HelpCircle className="h-4 w-4 mr-3 text-gray-400" />
              <span className="text-sm">Help Center</span>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-500" />
          </Link>
        </nav>

        {/* Sign out button */}
        <div className="mt-auto pt-6 border-t border-[#30363d]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }}
            className="w-full flex items-center p-2 rounded-lg text-gray-300 hover:bg-red-500/20 hover:text-red-300 transition-colors"
          >
            <LogOut className="h-4 w-4 mr-3 text-gray-400" />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  );
}
